import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Brain, FileText, TrendingUp, CheckCircle2, ArrowRight, Sparkles, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { SEONavbar, SEOFooter } from '../components/SEOLayout';
import PublicChatWidget from '../components/PublicChatWidget';

const FEATURES = [
  { icon: Brain, title: 'AI som läser ritningar och PDF-er', text: 'Ladda upp bygglovsritningar, anbudsunderlag eller specifikationer — Operio extraherar mängder, dimensioner och materialbehov automatiskt.' },
  { icon: Calculator, title: 'Färdig materiallista på 30 sekunder', text: 'Beräknar betong, armering, isolering, trä, gips m.m. baserat på din arbetstyp. Justera mängder och priser direkt i tabellen.' },
  { icon: FileText, title: 'Offert klar för signering', text: 'Auto-generera prisbjudande med ROT-avdrag, marginal och svensk standard. Skicka direkt till kund med BankID-signering.' },
  { icon: TrendingUp, title: 'Lär sig av dina projekt', text: 'Varje signerad offert förbättrar AI:n. Efter 5–10 projekt blir uppskattningarna 30–40% mer exakta för just ditt företag.' },
];

const FAQ = [
  { q: 'Vad är ett bygg kalkyl­program?', a: 'Ett bygg kalkyl­program (även kallat anbuds­program eller kalkylsystem) hjälper dig att räkna fram material­mängder, arbets­tid och totalkostnad för ett byggprojekt. Operio använder AI för att göra detta 10–20× snabbare än manuell kalkylering i Excel.' },
  { q: 'Klarar Operio AI både bygg, mark och VVS?', a: 'Ja. Operio är tränat på svenska bygg-, mark- och anläggnings­projekt. Du anger bara typ av jobb (grundläggning, schakt, fasad­renovering, vattenledning m.fl.) så genereras rätt materialset.' },
  { q: 'Hur exakt blir kalkylen?', a: 'För standardiserade arbeten (grund, fasad, ROT) ligger Operios AI inom ±10% av slutkostnad. För komplexa unika projekt rekommenderar vi att du justerar AI-förslaget manuellt innan offert skickas.' },
  { q: 'Kostar det extra att räkna med AI?', a: 'Nej. Alla AI-funktioner ingår i din månads­prenumeration utan extra avgifter — räkna obegränsat många kalkyler.' },
  { q: 'Kan jag exportera kalkylen till Excel eller Fortnox?', a: 'Ja. Operio exporterar till XLSX och har planerad integration mot Fortnox & Bokio (rullas ut Q2 2026).' },
];

const ByggKalkylPage = () => (
  <div className="min-h-screen" style={{ backgroundColor: '#F7F4EF' }}>
    <SEO
      title="Bygg kalkyl­program med AI – beräkna material och offert på 30 sek | Operio"
      description="Operios AI-drivna bygg kalkylprogram läser ritningar, beräknar materialmängder och genererar färdiga offerter med ROT-avdrag. 14 dagar gratis. För byggare, markentreprenörer & VVS."
      canonical="/bygg-kalkyl-program"
      keywords="bygg kalkyl program, kalkyl bygg entreprenad, offertverktyg bygg, anbudsverktyg bygg, AI bygg kalkyl, materialberäkning bygg"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Operio Bygg Kalkyl',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web, iOS, Android (PWA)',
        offers: { '@type': 'Offer', price: '1990', priceCurrency: 'SEK', priceValidUntil: '2026-12-31' },
        description: 'AI-drivet kalkyl- och offert­program för svenska bygg-, mark- och entreprenad­företag.',
        aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', reviewCount: '47' },
        featureList: 'AI material­beräkning, ritnings­analys, ROT-avdrag, offertskapande, BankID-signering',
      }}
    />
    <SEONavbar activePage="kalkyl" />

    <main className="pt-24 sm:pt-28 pb-20">
      <section className="px-5 sm:px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="inline-flex items-center gap-1.5 text-sm font-medium text-[#E8613A] uppercase tracking-widest mb-4">
            <Sparkles size={14} /> AI för svenska byggare
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A1A1A] leading-[1.05]"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Bygg kalkyl­program som räknar offerten åt dig
          </h1>
          <p className="text-base sm:text-lg text-[#6B6560] mt-5 max-w-2xl mx-auto leading-relaxed">
            Operio läser dina ritningar, beräknar material­mängder och skapar färdiga prisbjudanden
            med ROT-avdrag — på under en minut. Ingen mer halv natt i Excel.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link to="/register"><Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-12 px-8 text-base rounded-full shadow-lg shadow-[#E8613A]/30">Räkna första offerten gratis <ArrowRight size={18} className="ml-2" /></Button></Link>
            <Link to="/priser"><Button variant="outline" className="h-12 px-8 text-base rounded-full">Se priser</Button></Link>
          </div>
          <p className="text-xs text-[#7A7570] mt-4">14 dagar gratis · inga kortuppgifter krävs · säg upp när som helst</p>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-20 sm:mt-28">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Så funkar AI-kalkyl i Operio
            </h2>
            <p className="text-[#6B6560] mt-3 max-w-2xl mx-auto">Fyra steg från ritning till signerad offert.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <article key={i} className="bg-white border border-[#E8E2D9] rounded-2xl p-6">
                  <div className="w-11 h-11 bg-[#E8613A]/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon size={20} className="text-[#E8613A]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1A1A1A]">{f.title}</h3>
                  <p className="text-sm text-[#6B6560] mt-2 leading-relaxed">{f.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-20 sm:mt-28">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-[#E8E2D9] p-8 sm:p-12">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Spara 6–10 timmar per offert
          </h2>
          <p className="text-[#6B6560] mt-3 max-w-2xl">
            Genomsnittliga byggare lägger 4–8 timmar på att räkna en offert i Excel. Operio gör samma jobb på under 60 sekunder
            och du vinner fler upphandlingar genom att hinna lämna in fler anbud.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            {[
              { stat: '60s', label: 'Genomsnittlig kalkyl­tid' },
              { stat: '±10%', label: 'AI-precision på standardarbeten' },
              { stat: '3×', label: 'Fler offerter per vecka' },
            ].map((s) => (
              <div key={s.label} className="text-center bg-[#F7F4EF] rounded-xl p-5">
                <p className="text-4xl font-bold text-[#E8613A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{s.stat}</p>
                <p className="text-xs text-[#6B6560] mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-20 sm:mt-28">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A] mb-8 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Vanliga frågor
          </h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="group bg-white border border-[#E8E2D9] rounded-xl px-5 py-4 open:shadow-sm">
                <summary className="cursor-pointer text-base font-bold text-[#1A1A1A] flex justify-between items-center list-none">
                  <span className="pr-4">{f.q}</span>
                  <span className="text-[#E8613A] text-xl flex-shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-sm text-[#6B6560] mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-20">
        <div className="max-w-4xl mx-auto bg-[#1A1A1A] rounded-2xl px-6 py-12 sm:px-12 sm:py-14 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Räkna din första offert med AI idag
          </h2>
          <p className="text-white/70 mt-3 max-w-md mx-auto text-sm">14 dagar gratis. Importera dina priser, ladda upp en ritning och få färdig offert på minuten.</p>
          <Link to="/register" className="inline-block mt-6">
            <Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-12 px-8 text-base rounded-full shadow-lg shadow-[#E8613A]/30">Starta gratis <ArrowRight size={18} className="ml-2" /></Button>
          </Link>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-6 text-xs text-white/50">
            <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} /> Inga kortuppgifter</span>
            <span className="inline-flex items-center gap-1"><Clock size={12} /> 5 min setup</span>
            <span className="inline-flex items-center gap-1"><Shield size={12} /> GDPR & BankID</span>
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-16">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Läs vidare</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <Link to="/tidrapportering-bygg" className="bg-white border border-[#E8E2D9] rounded-xl p-4 hover:border-[#E8613A]/40 transition-colors">
              <p className="font-bold text-[#1A1A1A]">Tidrapportering för bygg</p>
              <p className="text-[#6B6560] text-xs mt-1">GPS-stämpling, OB-regler &amp; löner.</p>
            </Link>
            <Link to="/blogg/basta-bygg-kalkyl-program-2026" className="bg-white border border-[#E8E2D9] rounded-xl p-4 hover:border-[#E8613A]/40 transition-colors">
              <p className="font-bold text-[#1A1A1A]">Bästa bygg kalkyl­programmen 2026</p>
              <p className="text-[#6B6560] text-xs mt-1">Vi jämför 6 verktyg.</p>
            </Link>
            <Link to="/priser" className="bg-white border border-[#E8E2D9] rounded-xl p-4 hover:border-[#E8613A]/40 transition-colors">
              <p className="font-bold text-[#1A1A1A]">Priser från 1 990 kr/mån</p>
              <p className="text-[#6B6560] text-xs mt-1">Tre paket — börja gratis.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>

    <SEOFooter />
    <PublicChatWidget />
  </div>
);

export default ByggKalkylPage;
